'use client';

import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import type { HubRegulation, HubRegulationsPaginated, HubTopicWithStats } from '@justisio/shared';

interface RelatedPost {
  id: string;
  slug: string;
  title: string;
  featuredImage: string | null;
  publishedAt: string | null;
  createdAt: string | null;
  category: { name: string } | null;
  formattedDate: string;
}

interface FormattedHubRegulation extends HubRegulation {
  formattedDate: string;
}

interface HubPageClientProps {
  hub: Omit<HubTopicWithStats, 'relatedPosts'> & { relatedPosts: RelatedPost[] };
  regulations: Omit<HubRegulationsPaginated, 'regulations'> & {
    regulations: FormattedHubRegulation[];
  };
  currentPage: number;
}

function StatCard({
  label,
  value,
  className = '',
}: {
  label: string;
  value: string | number;
  className?: string;
}) {
  return (
    <div
      className={`bg-white rounded-xl shadow-[0_4px_20px_-4px_rgba(0,0,0,0.08)] border border-neutral-100 p-5 flex flex-col justify-between ${className}`}
    >
      <p className="text-3xl font-black text-neutral-900 mb-1 tracking-tight">
        {value.toLocaleString('id-ID')}
      </p>
      <p className="text-sm font-medium text-neutral-500">{label}</p>
    </div>
  );
}

function ArticleImage({ src, alt }: { src: string | null; alt: string }) {
  if (src) {
    return (
      <div className="relative aspect-video overflow-hidden bg-neutral-100 rounded-lg">
        <Image src={src} alt={alt} fill sizes="33vw" className="object-cover" />
      </div>
    );
  }

  return (
    <div className="relative aspect-video bg-gradient-to-br from-yellow-400 via-orange-400 to-red-400 rounded-lg flex items-center justify-center">
      <span className="text-white text-sm font-bold opacity-30 tracking-widest">JUSTISIO</span>
    </div>
  );
}

function getPageNumbers(currentPage: number, totalPages: number): Array<number | 'ellipsis'> {
  const pages: Array<number | 'ellipsis'> = [];
  const maxVisible = 5;

  if (totalPages <= maxVisible + 2) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
  } else {
    pages.push(1);

    if (currentPage > 3) {
      pages.push('ellipsis');
    }

    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (currentPage < totalPages - 2) {
      pages.push('ellipsis');
    }

    pages.push(totalPages);
  }

  return pages;
}

/** Dark hero band with breadcrumb, hub title, and intro content. */
function HubHeroSection({ hub }: { hub: HubPageClientProps['hub'] }) {
  return (
    <section className="bg-gradient-to-br from-neutral-900 via-neutral-800 to-neutral-900 text-white pt-28 pb-16 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-2 text-neutral-400 text-xs uppercase tracking-wider mb-4">
          <Link href="/blog" className="hover:text-white transition-colors">
            Blog
          </Link>
          <span>/</span>
          <span className="text-yellow-400">Hub</span>
        </div>

        <h1 className="text-3xl md:text-4xl font-bold mb-4">{hub.heroTitle || hub.name}</h1>

        {hub.heroContent && (
          <div className="prose prose-invert max-w-3xl">
            <p className="text-neutral-300 text-lg leading-relaxed">{hub.heroContent}</p>
          </div>
        )}

        {hub.description && !hub.heroContent && (
          <p className="text-neutral-300 text-lg">{hub.description}</p>
        )}
      </div>
    </section>
  );
}

/** Full-width grid of recent kajian articles related to the hub. */
function HubRelatedPostsSection({ relatedPosts }: { relatedPosts: RelatedPost[] }) {
  if (relatedPosts.length === 0) return null;

  return (
    <section className="max-w-6xl mx-auto px-4 py-12">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-bold text-neutral-900">Kajian Terkait</h2>
        <Link
          href="/blog/posts"
          className="text-xs font-semibold uppercase tracking-wider text-yellow-600 hover:text-yellow-700 flex items-center gap-1"
        >
          Lihat Semua
          <ArrowRight className="w-3 h-3" />
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {relatedPosts.map((post) => (
          <Link key={post.id} href={`/blog/${post.slug}`} className="group">
            <ArticleImage src={post.featuredImage} alt={post.title} />
            <div className="mt-3">
              {post.category && (
                <span className="text-[9px] font-bold uppercase tracking-wider text-amber-700">
                  {post.category.name}
                </span>
              )}
              <h3 className="text-sm font-semibold leading-snug mt-1 group-hover:underline decoration-1 underline-offset-2 line-clamp-2">
                {post.title}
              </h3>
              <p className="text-[10px] text-neutral-400 mt-1">{post.formattedDate}</p>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

/** Regulation table with pagination controls. */
function HubRegulationsSection({
  hub,
  regulations,
  currentPage,
}: {
  hub: HubPageClientProps['hub'];
  regulations: HubPageClientProps['regulations'];
  currentPage: number;
}) {
  const totalPages = regulations.totalPages;
  const startItem = (currentPage - 1) * regulations.limit + 1;
  const endItem = Math.min(currentPage * regulations.limit, regulations.total);
  const pageNumbers = getPageNumbers(currentPage, totalPages);

  return (
    <section id="regulations" className="max-w-6xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-bold text-neutral-900">Peraturan Terbaru</h2>
        <p className="text-xs text-neutral-500">
          {startItem}-{endItem} dari {regulations.total.toLocaleString('id-ID')} peraturan
        </p>
      </div>

      <div className="bg-white rounded-xl border border-neutral-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-neutral-50 border-b border-neutral-100">
              <tr>
                <th className="text-left text-[10px] font-bold uppercase tracking-wider text-neutral-600 px-4 py-3 w-full">
                  Judul
                </th>
                <th className="text-left text-[10px] font-bold uppercase tracking-wider text-neutral-600 px-4 py-3 w-36 whitespace-nowrap">
                  Tanggal
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-50">
              {regulations.regulations.map((reg) => (
                <tr key={reg.regulationId} className="hover:bg-neutral-50 transition-colors">
                  <td className="px-4 py-4">
                    <Link
                      href={`/regulation/indonesia/${reg.regulationId}`}
                      className="text-sm text-neutral-800 hover:text-blue-600 hover:underline leading-relaxed block"
                      title={reg.regulationTitle || '-'}
                    >
                      {reg.regulationTitle || '-'}
                    </Link>
                  </td>
                  <td className="px-4 py-4 w-36 whitespace-nowrap align-top">
                    <span className="text-sm text-neutral-500 font-medium">{reg.formattedDate}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {regulations.regulations.length === 0 && (
          <div className="px-4 py-12 text-center text-neutral-500">Belum ada data peraturan</div>
        )}

        {totalPages > 1 && (
          <div className="px-4 py-3 border-t border-neutral-100 flex items-center justify-center gap-1">
            {currentPage > 1 ? (
              <Link
                href={`/blog/hub/${hub.slug}?page=${currentPage - 1}#regulations`}
                className="p-2 rounded-lg text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900"
              >
                <ChevronLeft className="w-4 h-4" />
              </Link>
            ) : (
              <span className="p-2 rounded-lg text-neutral-300 cursor-not-allowed">
                <ChevronLeft className="w-4 h-4" />
              </span>
            )}

            {pageNumbers.map((page, idx) =>
              page === 'ellipsis' ? (
                <span key={`ellipsis-${idx}`} className="px-2 text-neutral-400">
                  ...
                </span>
              ) : (
                <Link
                  key={page}
                  href={`/blog/hub/${hub.slug}${page === 1 ? '' : `?page=${page}`}#regulations`}
                  className={`min-w-[32px] h-8 px-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center ${
                    page === currentPage
                      ? 'bg-neutral-900 text-white'
                      : 'text-neutral-600 hover:bg-neutral-100'
                  }`}
                >
                  {page}
                </Link>
              )
            )}

            {currentPage < totalPages ? (
              <Link
                href={`/blog/hub/${hub.slug}?page=${currentPage + 1}#regulations`}
                className="p-2 rounded-lg text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900"
              >
                <ChevronRight className="w-4 h-4" />
              </Link>
            ) : (
              <span className="p-2 rounded-lg text-neutral-300 cursor-not-allowed">
                <ChevronRight className="w-4 h-4" />
              </span>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

/** Promotional banner pointing users to the AI research agent. */
function HubCtaSection({ hubName }: { hubName: string }) {
  return (
    <section className="max-w-6xl mx-auto px-4 py-8 pb-16">
      <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl p-6 border border-yellow-100">
        <h3 className="text-lg font-bold text-neutral-900 mb-2">
          Butuh bantuan memahami {hubName}?
        </h3>
        <p className="text-sm text-neutral-600 mb-4">
          Gunakan fitur AI Justisio untuk menganalisis dan memahami peraturan dengan lebih mudah.
        </p>
        <Link
          href="/id/regulation/research-agent"
          className="inline-flex items-center gap-2 bg-neutral-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-neutral-800 transition-colors"
        >
          Mulai Chat dengan AI
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </section>
  );
}

export default function HubPageClient({ hub, regulations, currentPage }: HubPageClientProps) {
  const { stats, relatedPosts } = hub;

  return (
    <div className="min-h-screen bg-neutral-50">
      <HubHeroSection hub={hub} />

      <section className="max-w-6xl mx-auto px-4 -mt-8">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <StatCard label="Total Peraturan" value={stats.totalRegulations} />
          <StatCard label="Berlaku" value={stats.inEffectCount} />
          <StatCard label="Tidak Berlaku" value={stats.notInEffectCount} />
          <StatCard
            label={`Diterbitkan ${new Date().getFullYear()}`}
            value={stats.thisYearCount}
          />
          <StatCard
            label="Konsolidasi Peraturan"
            value={stats.consolidationCount}
            className="col-span-2 md:col-span-1 mx-auto w-[calc(50%-0.5rem)] md:w-full md:mx-0"
          />
        </div>
      </section>

      <HubRelatedPostsSection relatedPosts={relatedPosts} />

      <HubRegulationsSection hub={hub} regulations={regulations} currentPage={currentPage} />

      <HubCtaSection hubName={hub.name} />
    </div>
  );
}
